import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormFTB3885 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="File CA FTB Form 3885F for Depreciation with TaxZerone"
          description="Learn about FTB Form 3885F, required for reporting depreciation and amortization in California. File Form 3885F easily with TaxZerOne in just 3 steps."
          keywords=""
          canonical="resources/tax-exempt/ftb-form-3885f-depreciation/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          FTB{' '}
          <span className="highlight-container">
            <span className="highlight">Form 3885F </span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/ftb-form-3885f-depreciation/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    FTB Form 3885F at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form FTB 3885F? Form FTB 3885F, Depreciation
                          and Amortization,
                        </strong>{' '}
                        is used to calculate{' '}
                        <strong style={{ fontWeight: 600 }}>
                          California depreciation and amortization deductions
                          for fiduciaries and trusts
                        </strong>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses It? Estates and trusts
                        </strong>{' '}
                        generally use FTB 3885F when California depreciation or
                        amortization must be calculated under the applicable tax
                        rules.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        FTB 3885F reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          depreciation, amortization, asset basis, recovery
                          periods
                        </strong>
                        , and applicable{' '}
                        <strong style={{ fontWeight: 600 }}>
                          California depreciation adjustments
                        </strong>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:
                        </strong>{' '}
                        FTB 3885F may support{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/efile-ftb-ca-form109-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 109
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/efile-ftb-ca-form199-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 199
                        </Link>{' '}
                        when depreciation and amortization information is
                        required for the California return.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Introduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                   <strong style={{ fontWeight: 600 }}> FTB Form 3885F</strong>, <i> Depreciation and Amortization</i>,
                  is a tax form used by corporations that are subject to
                  California&apos;s corporate income tax. This form is
                  specifically for reporting depreciation and amortization
                  expenses related to property used in the taxpayer&apos;s trade
                  or business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this resource article, we will help you understand FTB Form
                  3885F by clarifying who must file, the filing requirements,
                  and addressing commonly asked questions.
                </Typography> */}

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is FTB 199 - Form 3885F?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file FTB 199 - Form 3885F?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Form 3885F with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#source" sx={{ color: '#0202ff' }}>
                          Source and Reference
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is FTB - Form 3885F?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Form 3885F</strong> is a
                  supplementary form to the{' '}
                  <Link
                    href="/tax-exempt/efile-ftb-ca-form199-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    FTB 199
                  </Link>
                  , filed by tax-exempt organizations in California. This form
                  is used to
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    compute depreciation and amortization
                  </strong>{' '}
                  allowed as a deduction on Form 199, California Exempt
                  Organization Annual Information Return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  It allows the filing organizations to claim deductions for the
                  depreciation and amortization of tangible and intangible
                  property, which includes physical assets like buildings and
                  machinery as well as intangible assets such as patents and
                  trademarks.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong>{' '}
                  Depreciation is the allocation of the cost of a tangible asset
                  over its useful life, while amortization is the allocation of
                  the cost of an intangible asset over its useful life.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file FTB 199 - Form 3885F?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Exempt organizations in California, including 501(c)(3)
                  entities, religious organizations, and certain other
                  nonprofits, must file Form 3885F if they are claiming
                  deductions for depreciable or amortizable property on their
                  FTB 199.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Typically, organizations that hold and use tangible or
                  intangible property in their operations and are entitled to
                  depreciation or amortization deductions should file this form
                  to ensure compliance with California tax laws.
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ mb: 0, fontWeight: 600 }}
                      >
                        Choose TaxZerone for a filing experience that goes
                        beyond expectations
                      </Typography>
                      <Typography variant="body2">
                        Empower your tax compliance journey with us today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=ftb3885f`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                   <strong style={{ fontWeight: 600 }}>Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to file Form 3885F with TaxZerone
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885f-depreciation-img.webp" />
                  <source srcSet="/assets/images/f3885f-depreciation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885f-depreciation-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3885F Depreciation and amortization"
                    width={760}
                    height={111}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This header section of Form 3885F is simply the identification
                  part of the form.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Name as shown on tax return
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write the exact name of the taxpayer or organization as it
                  appears on the main tax return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  FEIN
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Federal Employer Identification Number, which is the
                  organization’s tax ID number assigned by the Internal Revenue
                  Service.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885f-cta-tangible-img.webp" />
                  <source srcSet="/assets/images/f3885f-cta-tangible-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885f-cta-tangible-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3885F Depreciation and amortization for tangible and intangible service Line1"
                    width={760}
                    height={150}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Complete columns (a) through (i) for each asset, or group of
                  similar assets, that was placed in service during the 2025 tax
                  year. Then total the amounts in column (f) and enter it on
                  line 1(f), and total column (i) and enter it on line 1(i).
                  Attach a separate schedule if more space is needed.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This column is used to write a short description of the asset
                  placed in service during the year, such as a computer, office
                  furniture, machinery, or software.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date when the asset was first put to use in the
                  business. This is the day the asset became ready and started
                  being used for business activities.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write the cost of the asset here. This generally includes the
                  purchase price and any additional expenses needed to get the
                  asset ready to use, such as installation or setup costs.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This column indicates the depreciation method used to
                  calculate how the asset’s cost will be spread over its useful
                  life.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This column shows the useful life of the asset or the
                  depreciation rate used to determine how long the cost will be
                  deducted.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This column reports the depreciation amount that can be
                  claimed for the current tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This column is used to mention the relevant tax code section
                  if a specific rule applies to the asset.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (h)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This column shows the amortization period or percentage used
                  for intangible assets.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Column (i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This column records the amortization amount that can be
                  deducted for the current year.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885f-cta-line2-3-img.webp" />
                  <source srcSet="/assets/images/f3885f-cta-line2-3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885f-cta-line2-3-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3885F Depreciation of tangible and intangible service Line 2 and Line 3"
                    width={760}
                    height={80}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Line 2
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the California depreciation for assets that were
                    placed in service before the 2025 tax year.
                  </li>
                  <li>
                    If there are any differences between federal and California
                    basis amounts, make sure those adjustments are included when
                    calculating this amount.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the depreciation amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    line 1(f) and line 2.
                  </span>{' '}
                  The total represents the overall California depreciation
                  deduction for the year in Form 3885F.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885f-cta-line4-6-img.webp" />
                  <source srcSet="/assets/images/f3885f-cta-line4-6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885f-cta-line4-6-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3885F Amortization of tangible and intangible service Line 4 to Line 6"
                    width={760}
                    height={98}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the California amortization for intangible assets that
                  were placed in service before the 2025 tax year. If there are
                  any differences between the federal and California basis,
                  adjust the amount accordingly.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amortization{' '}
                  <span style={{ fontWeight: 600 }}>
                    amount from line 1(i) and line 4.
                  </span>{' '}
                  This gives the total California amortization for the year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.6 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>line 3 and line 5.</span>{' '}
                  The results show the total depreciation and amortization
                  deduction for the year reported on Form 3885F.
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, scrollMarginTop: 88 }}
                  id="source"
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on official California{' '}
                        <Link
                          href="https://www.ftb.ca.gov/forms/2025/2025-3885f.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          FTB Instruction of Form 3885F
                        </Link>
                        . For complete and updated information, refer to the
                        official California Franchise Tax Board website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Need to e-file Form 3885-F?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure your farm-related assets are correctly reported for
              California depreciation{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              and amortization under California Franchise Tax Board rules.
              TaxZerone helps you calculate{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              California depreciation adjustments and easily attach Form 3885F
              to your
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              <Link
                href="/tax-exempt/efile-ftb-ca-form199-online/"
                sx={{ color: '#0202ff' }}
              >
                {' '}
                Form 199
              </Link>
              - quickly, securely, and with confidence.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form3885f`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormFTB3885;
